import React from "react";
import { LOGO_URL } from "../../lib/brand";
import { COMPANY } from "../../lib/jobConstants";
import { money } from "../../lib/jobCalc";

const TERMS_LABEL = { cod: "C.O.D.", "30_days": "Nett 30 days", "60_days": "Nett 60 days" };

/**
 * Print-ready customer statement in the same visual language as the six paperwork
 * documents. Uses the app's existing `printing-doc` class to strip chrome when the
 * admin clicks Print / Save as PDF.
 */
export const CustomerStatementDoc = ({ statement }) => {
  if (!statement) return null;
  const c = statement.customer || {};
  const lines = statement.lines || [];
  const today = new Date().toLocaleDateString("en-ZA");

  return (
    <article className="bg-white border border-[#cfd7db] rounded-md p-8 md:p-10 shadow-sm max-w-5xl mx-auto" data-testid="statement-doc">
      {/* Header */}
      <header className="flex items-start justify-between gap-6 pb-6 border-b-2 border-[#111619]">
        <div className="flex items-center gap-4">
          <img src={LOGO_URL} alt="" className="h-16 w-auto object-contain" />
        </div>
        <div className="text-right">
          <h1 className="font-display font-black uppercase text-3xl text-[#111619] leading-none">Statement</h1>
          <div className="text-xs text-[#3f4b52] mt-2">
            {COMPANY.name} · {COMPANY.tradingAs}
          </div>
          <div className="text-xs text-[#3f4b52]">
            {COMPANY.email} · {COMPANY.phone}
          </div>
        </div>
      </header>

      {/* Customer + statement meta */}
      <section className="grid grid-cols-1 sm:grid-cols-2 gap-6 mt-6 text-sm">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#3f4b52]">Statement to</div>
          <div className="font-display font-black uppercase text-lg text-[#111619] mt-1">
            <span className="text-[#f28a16] mr-2">{c.code}</span>{c.name}
          </div>
          {c.id_or_reg_number && <div className="text-[#3f4b52]">Reg No: {c.id_or_reg_number}</div>}
          {c.vat_number && <div className="text-[#3f4b52]">VAT No: {c.vat_number}</div>}
          {c.billing_address && <div className="text-[#3f4b52] mt-1 whitespace-pre-line">{c.billing_address}</div>}
          {c.email && <div className="text-[#3f4b52]">{c.email}</div>}
          {c.mobile && <div className="text-[#3f4b52]">{c.mobile}</div>}
        </div>
        <div className="text-sm text-[#3f4b52] sm:text-right">
          <div><span className="font-display uppercase tracking-widest text-xs">Generated</span> · <b className="text-[#111619]">{today}</b></div>
          {statement.date_from && (
            <div className="mt-1"><span className="font-display uppercase tracking-widest text-xs">Period from</span> · <b className="text-[#111619]">{statement.date_from}</b></div>
          )}
          {statement.date_to && (
            <div><span className="font-display uppercase tracking-widest text-xs">Period to</span> · <b className="text-[#111619]">{statement.date_to}</b></div>
          )}
          <div className="mt-1"><span className="font-display uppercase tracking-widest text-xs">Terms</span> · <b className="text-[#111619]">{TERMS_LABEL[c.account_terms] || c.account_terms}</b></div>
        </div>
      </section>

      {/* Ledger */}
      <section className="mt-8">
        <table className="w-full text-sm border border-[#cfd7db]">
          <thead className="bg-[#111619] text-white uppercase tracking-widest text-xs">
            <tr>
              <th className="text-left px-3 py-2">Date</th>
              <th className="text-left px-3 py-2">Reference</th>
              <th className="text-left px-3 py-2">Description</th>
              <th className="text-right px-3 py-2">Debit</th>
              <th className="text-right px-3 py-2">Credit</th>
              <th className="text-right px-3 py-2">Balance</th>
            </tr>
          </thead>
          <tbody>
            <tr className="bg-[#f5f6f6]">
              <td className="px-3 py-2 text-[#3f4b52]" colSpan={5}>
                <b>Opening balance</b>{statement.date_from ? ` — as at ${statement.date_from}` : ""}
              </td>
              <td className="px-3 py-2 text-right font-bold text-[#111619]">{money(statement.opening_balance)}</td>
            </tr>
            {lines.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-3 py-6 text-center text-[#3f4b52]">No transactions in this period.</td>
              </tr>
            ) : (
              lines.map((ln, i) => (
                <tr key={i} className={ln.type === "payment" ? "bg-[#ecfdf5]" : ""}>
                  <td className="px-3 py-2 text-[#3f4b52]">{ln.date || "—"}</td>
                  <td className="px-3 py-2 font-mono">{ln.reference}</td>
                  <td className="px-3 py-2 text-[#3f4b52]">{ln.description}</td>
                  <td className="px-3 py-2 text-right">{ln.debit ? money(ln.debit) : ""}</td>
                  <td className="px-3 py-2 text-right text-[#10b981]">{ln.credit ? money(ln.credit) : ""}</td>
                  <td className="px-3 py-2 text-right font-bold">{money(ln.balance)}</td>
                </tr>
              ))
            )}
            <tr className="bg-[#111619] text-white">
              <td className="px-3 py-2 uppercase tracking-widest text-xs" colSpan={5}>
                <b>Closing balance</b>{statement.date_to ? ` — as at ${statement.date_to}` : ""}
              </td>
              <td className="px-3 py-2 text-right font-display font-black text-lg">{money(statement.closing_balance)}</td>
            </tr>
          </tbody>
        </table>
      </section>

      {/* Ageing snapshot */}
      <section className="mt-8 grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetaCard label="Total billed" value={money(lines.reduce((a, l) => a + (l.debit || 0), 0))} />
        <MetaCard label="Total paid" value={money(lines.reduce((a, l) => a + (l.credit || 0), 0))} color="#10b981" />
        <MetaCard label="Opening" value={money(statement.opening_balance)} />
        <MetaCard label="Amount due" value={money(statement.closing_balance)} color={statement.closing_balance > 0 ? "#dc2626" : "#10b981"} />
      </section>

      {/* Footer */}
      <footer className="mt-10 pt-6 border-t border-[#cfd7db] text-xs text-[#3f4b52] grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <div className="font-display uppercase tracking-widest text-xs text-[#111619]">Payment details</div>
          <div className="mt-1">Bank: <b>{COMPANY.bank}</b></div>
          <div>Branch: <b>{COMPANY.branch}</b></div>
          <div>Reference: <b>{c.code}</b></div>
        </div>
        <div className="sm:text-right">
          <div className="font-display uppercase tracking-widest text-xs text-[#111619]">Queries</div>
          <div className="mt-1">{COMPANY.email}</div>
          <div>{COMPANY.phone}</div>
        </div>
      </footer>
      <p className="mt-6 text-xs text-[#9aa6ad] text-center">
        E&amp;OE. This statement reflects transactions on record as at the generation date. Please contact us if you believe there is a discrepancy.
      </p>
    </article>
  );
};

const MetaCard = ({ label, value, color = "#111619" }) => (
  <div className="border border-[#cfd7db] rounded-md p-3">
    <div className="text-xs uppercase text-[#3f4b52] font-display tracking-widest">{label}</div>
    <div className="font-display font-black text-lg mt-0.5" style={{ color }}>{value}</div>
  </div>
);

export default CustomerStatementDoc;
